import { useAppModule } from '../appContext.ts'
import { Link, NavLink, useNavigate } from 'react-router-dom'
import { urls } from '../../shared/urls.ts'
import { Icon } from '../../shared/ui/icon/icon.tsx'
import { cn } from '../../shared/lib/utils.ts'
import { useSnapshot } from 'valtio/react'
import { FormattedMessage } from 'react-intl'
import { isNavigationItemVisible } from '../../shared/data/courseAvailability.ts'

const menuItemsBase = [
  {
    text: 'navbar.lessons',
    id: 'navbar-lessons',
    icon: 'navStar',
    url: urls.learningProfile(),
  },
  {
    text: 'navbar.vocabPlus',
    id: 'navbar-vocabPlus',
    icon: 'book',
    url: urls.practice,
  },
  {
    text: 'navbar.speaking',
    id: 'navbar-speaking',
    icon: 'mic',
    url: urls.speaking,
  },
  {
    text: 'navbar.testMode',
    id: 'navbar-testMode',
    icon: 'history',
    url: urls.testMode,
  },
] as const

const navLinkClassName = (params: { isActive: boolean }) =>
  cn(
    'flex min-h-44 items-center gap-12 rounded-12 px-14 text-14 font-semibold transition-colors 2xl:min-h-60 2xl:gap-16 2xl:rounded-18 2xl:px-18 2xl:text-18',
    params.isActive
      ? 'bg-white font-bold text-main shadow-button'
      : 'text-gray-dark hover:bg-white/60 hover:text-black',
  )

export const Menu = (props: { className?: string }) => {
  const module = useAppModule()
  const authStore = module.authStore
  const authState = useSnapshot(authStore.state)
  const appState = useSnapshot(module.appStore.state)
  const isAnonOrLogout = authState.isAnon || !authState.isLogin
  const currentCourse = module.appStorage.get('currentCourse')
  const currentCourseName = currentCourse?.name ?? '—'
  const uiLocale = appState.uiLocale
  const menuItems = menuItemsBase.filter((item) =>
    isNavigationItemVisible(item.text, currentCourse?.id, uiLocale),
  )

  return (
    <aside
      aria-label="main menu"
      className={cn(
        'sticky top-0 flex h-dvh w-[220px] shrink-0 cursor-default flex-col overflow-y-auto pt-48 text-14 2xl:w-[300px] 2xl:pt-64 2xl:text-18',
        props.className,
      )}
    >
      <div className="flex min-h-full flex-col gap-28 px-6 pb-40 2xl:gap-40 2xl:px-8 2xl:pb-56">
        <Link to="/" className="px-6 2xl:px-8">
          <img
            src="/images/empatika-logo-horizontal-color.svg"
            alt="Эмпатика"
            className="h-28 w-auto max-w-full object-contain object-left 2xl:h-36"
          />
        </Link>

        <nav>
          <ul className="flex list-none flex-col gap-4 p-0 2xl:gap-8">
            {menuItems.map((item) => (
              <li key={item.text}>
                <NavLink
                  to={item.url}
                  id={item.id}
                  className={navLinkClassName}
                >
                  <Icon className="shrink-0" size="sm" iconName={item.icon} />
                  <FormattedMessage id={item.text} />
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {!isAnonOrLogout ? (
          <>
            <div role="separator" className="mx-14 h-1 shrink-0 bg-gray" />
            <ul className="mt-auto flex list-none flex-col gap-4 p-0">
              <li>
                <NavLink className={navLinkClassName} to={urls.myCourses}>
                  <Icon iconName="book" size="sm" />
                  <div className="flex min-w-0 flex-col">
                    <span>
                      <FormattedMessage id="myCoursesButton" />
                    </span>
                    <span className="text-10 font-normal opacity-70">
                      <FormattedMessage
                        id="myCoursesCurrentCourseLabel"
                        values={{ courseName: currentCourseName }}
                      />
                    </span>
                  </div>
                </NavLink>
              </li>
              <li>
                <NavLink className={navLinkClassName} to={urls.profile()}>
                  <Icon iconName="profile2" size="sm" />
                  <FormattedMessage id="navbar.profile" />
                </NavLink>
              </li>
              <li>
                <NavLink className={navLinkClassName} to={urls.contacts}>
                  <Icon iconName="help" size="sm" />
                  <FormattedMessage id="Help" />
                </NavLink>
              </li>
              <li>
                <LoginLogout />
              </li>
            </ul>
          </>
        ) : null}
      </div>
    </aside>
  )
}

function LoginLogout() {
  const authStore = useAppModule().authStore
  const navigate = useNavigate()
  const state = useSnapshot(authStore.state)
  const logout = state.isLogin && !state.isAnon

  return (
    <button
      data-testid="logout-btn"
      type="button"
      className={navLinkClassName({ isActive: false })}
      onClick={() => {
        if (logout) {
          authStore.logoutAndRefresh()
        } else {
          navigate(urls.login)
        }
      }}
    >
      <Icon iconName="exit" size="sm" />
      <FormattedMessage id={logout ? 'Log out' : 'Log in'} />
    </button>
  )
}
